const myStorage = window.localStorage;

// Filled in init function
let userData;

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const id = urlParams.get("id");
const session = urlParams.get("session");
// Auth0 redirects back with error=access_denied when it refuses a Clarity token
// to an account without the Clarity User role.
const accessDenied = urlParams.get("error") === "access_denied";
const ordDetailOrigin = urlParams.get("type");
const mdmType = urlParams.get("mdm_type");
let data = "";
const hostedURL = window.location.origin;

/** @type {import('@auth0/auth0-spa-js').Auth0Client} */
let auth0Client = null;

const createAuth0Client = async () => {
    const response = await fetch(`${hostedURL}/auth0-config.json`);
    const config = await response.json();

    auth0Client = await auth0.createAuth0Client({
        domain: config.domain,
        clientId: config.clientId,
        authorizationParams: {
            audience: config.audience,
        },
        useRefreshTokens: true,
        cacheLocation: "localstorage",
    });
};

const MINUTES = 60 * 1000;
const IDLE_TIMEOUT_MS = 30 * MINUTES;
const ACTIVITY_KEY = "clarity_last_activity";

const logout = async () => {
    localStorage.removeItem(ACTIVITY_KEY);
    await auth0Client.logout();
};

// Wraps getTokenSilently with login_required handling so callers don't need to
const getToken = async () => {
    try {
        return await auth0Client.getTokenSilently({});
    } catch (err) {
        if (err.error === "login_required" || err.error === "missing_refresh_token") {
            window.location.href = "./index.html";
            return;
        }
        throw err;
    }
};

// Proactively refresh the token every 25 min so Auth0's server-side inactivity
// clock resets for active sessions (cacheMode:"off" forces a real token endpoint call)
const startTokenRefresh = () => {
    setInterval(async () => {
        try {
            await auth0Client.getTokenSilently({ cacheMode: "off" });
        } catch (err) {
            if (err.error === "login_required" || err.error === "missing_refresh_token") {
                window.location.href = "./index.html";
            }
        }
    }, 25 * MINUTES);
};

const startIdleTimer = () => {
    let idleTimer;

    const resetTimer = () => {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(logout, IDLE_TIMEOUT_MS);
    };

    const onActivity = () => {
        localStorage.setItem(ACTIVITY_KEY, Date.now());
        resetTimer();
    };

    // When another tab records activity, reset this tab's timer too
    window.addEventListener("storage", (e) => {
        if (e.key === ACTIVITY_KEY) {
            resetTimer();
        }
    });

    for (const event of ["keydown", "click", "scroll", "touchstart"]) {
        document.addEventListener(event, onActivity, { passive: true });
    }

    resetTimer();
};

//Initialize ====================================================================
const init = async (type, callback) => {
    await createAuth0Client();

    const query = window.location.search;

    if (query.includes("code=") && query.includes("state=")) {
        await auth0Client.handleRedirectCallback();
        window.location.href = "./";
        return;
    }

    const isAuthenticated = await auth0Client.isAuthenticated();

    if (isAuthenticated) {
        try {
            userData = (await sendRequest("get_user_data", "")).data;
        } catch (error) {
            console.error("Error fetching user data:", error);

            void auth0Client.loginWithRedirect({
                authorizationParams: {
                    redirect_uri: window.location.origin,
                },
            });
            return;
        }

        callback?.();
        startIdleTimer();
        startTokenRefresh();
    }

    document.getElementById("loading-overlay").style.display = "none";

    switch (type) {
        //Login
        case "login": {
            if (session) {
                showErrorMessage("login", session);
                return;
            }
            if (accessDenied) {
                showErrorMessage(
                    "login",
                    "This account isn't authorized to use Clarity. If you think this is a mistake, contact Client Support, or request an account below. To use a different account, log in again.",
                );
                return;
            }
            hideErrorMessage("login");

            if (isAuthenticated) {
                if (userData.terms_accepted) {
                    window.location.href = "./quote_list.html";
                } else {
                    window.location.href = "./terms_agreement.html";
                }
            }

            return;
        }

        //Account Request (Request a Quote)
        case "account_request": {
            renderAccountRequest();
            return;
        }

        //Partner Opportunity
        case "partner_opportunity": {
            renderPartnerOpportunity();
            return;
        }

        //Account Confirmation
        case "account_confirmation": {
            renderAccountConfirmation();
            return;
        }

        case "quote_request_new": {
            renderReqData();
            return;
        }
    }

    if (!isAuthenticated) {
        window.location.href = "./index.html";
        return;
    }

    switch (type) {
        //Terms Agreement
        case "terms_agreement": {
            renderTermsAgreement();
            return;
        }

        //Quote List
        case "quote_list": {
            renderQuoteList();
            break;
        }

        //Order List
        case "order_list": {
            renderOrderList();
            break;
        }

        //Quote Detail
        case "quote_detail": {
            renderQuote();
            break;
        }

        case "order_detail": {
            renderOrder();
            setTimeout(renderGraphics, 1000);
            break;
        }

        case "eval": {
            renderEval();
            break;
        }

        case "eval_detail": {
            renderEvalDetail();
            // sort('model');
            break;
        }

        case "guest_access": {
            renderGuestAccess();
            break;
        }

        case "manage_contacts": {
            renderOrgContacts();
            break;
        }

        case "messages": {
            renderMessage();
            break;
        }

        case "update_password": {
            break;
        }

        case "update_contact": {
            renderContactInfoData();
            break;
        }

        case "quote_request": {
            renderReqData();
            break;
        }

        case "quote_details": {
            renderQuoteDetails();
            break;
        }

        case "order_details": {
            renderOrderDetails();
            break;
        }

        case "sub_orders": {
            renderSubOrderList();
            break;
        }

        case "kandji_setup": {
            renderKandjiSetup();
            break;
        }

        case "jamf_setup": {
            renderJamfSetup();
            break;
        }

        case "mosyle_setup": {
            renderMosyleSetup();
            break;
        }

        case "mdm_device_list": {
            checkStatus();
            break;
        }

        case "full_clarity": {
            break;
        }

        case "mdm_processing": {
            checkStatus();
            break;
        }

        case "axm_setup": {
            checkStatus();
            break;
        }

        case "dev_import_setup": {
            renderDevImpSetup();
            break;
        }

        case "axm_skip": {
            break;
        }

        case "support_form": {
            initForm();
            break;
        }

        case "ds_device_list": {
            renderDSAssetList();
            break;
        }

        default:
            return;
    }

    checkForBetaAccess();
    checkForMobileScreenSize();
};
const checkForMobileScreenSize = () => {
    const winWidth = window.innerWidth;
    if (winWidth < 750) {
        window.location.href =
            "./index.html?session=Clarity%20does%20not%20support%20mobile%20browsers%20at%20this%20time.";
    }
};
const checkForBetaAccess = () => {
    const mdmBeta = document.getElementById("mdmBeta");
    if (userData.show_mdm !== 1 && mdmBeta) {
        mdmBeta.style.display = "none";
    }
    const depServ = document.getElementById("depServ");
    if (userData.show_ds !== 1 && depServ) {
        depServ.style.display = "none";
    }
};

//Send Forgot Password Email
const forgotPass = async () => {
    const email = document.getElementById("email-input").value;
    if (email === "") {
        showErrorMessage("login", "Please enter your email.");
        return;
    }
    const res2 = await sendRequest("forgot_pass_v2", email);
    showErrorMessage("login", res2.response.scriptResult);
};

//API Requests ==========================================================================
const sendRequest = async (script, param) => {
    const publicScripts = [
        "new_account_request",
        "partner_opportunity_v3",
        "confirm_account",
        "new_quote_request_v2",
    ];
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    if (!publicScripts.includes(script)) {
        const token = await getToken();
        myHeaders.append("Authorization", `Bearer ${token}`);
    }

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/scripts?script=${script}&param=${param}`,
        requestOptions,
    );
    return await response.json();
};

const getAssets = async (id, offset) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/assets?id=${id}&offset=${offset}`,
        requestOptions,
    );
    return await response.json();
};

const getDevices = async (id, offset, instance) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/devices?id=${id}&offset=${offset}&instance=${instance}`,
        requestOptions,
    );
    return await response.json();
};

const getDsAssets = async (id, offset, instance) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/ds-assets?id=${id}&offset=${offset}&instance=${instance}`,
        requestOptions,
    );
    return await response.json();
};

const createDsAssets = async (json) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/new-asset`, requestOptions);
    return await response.json();
};

const getSKU = async (id, offset, instance) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/sku?id=${id}&offset=${offset}&instance=${instance}`,
        requestOptions,
    );
    return await response.json();
};

const createRequest = async (json) => {
    // const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    // myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/requests`, requestOptions);
    return await response.json();
};

const createMDMRequest = async (json) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/mdm-requests`, requestOptions);
    return await response.json();
};

const createAXM = async (json) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/axm`, requestOptions);
    return await response.json();
};

const updateDsAsset = async (json, record) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "PATCH",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/ds-assets?record=${record}`, requestOptions);
    return await response.json();
};

const updateOrder = async (json, record) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "PATCH",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/orders?record=${record}`, requestOptions);
    return await response.json();
};

const updateCustomer = async (json, record) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "PATCH",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/customers?record=${record}`, requestOptions);
    return await response.json();
};

const createPartnerOpp = async (json) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch("https://clarity.diamondassets.com/partner", requestOptions);
    return await response.json();
};

const createDSAsset = async (json) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: json,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/new-asset`, requestOptions);
    return await response.json();
};

const downloadRequest = async (layout, id, file, filename) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/downloads?layout=${layout}&id=${id}&file=${file}`,
        requestOptions,
    );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
};

const uploadMailin = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/uploads?id=${id}&filetype=mail_template`,
        requestOptions,
    );
    return await response.text();
};

const uploadDSD = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/uploads?id=${id}&filetype=dep_serv_data`,
        requestOptions,
    );
    return await response.text();
};

const uploadReqTemplate = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", req_data.files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/uploads?id=${id}&filetype=request_template`,
        requestOptions,
    );
    return await response.text();
};

const uploadAXM = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", axmData.files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/uploads?id=${id}&filetype=axm`, requestOptions);
    return await response.text();
};

const uploadMDM = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", mdmData.files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/uploads?id=${id}&filetype=axm`, requestOptions);
    return await response.text();
};

const uploadReqTemplateNEW = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", req_data.files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/uploads?id=${id}&filetype=request_template`,
        requestOptions,
    );
    return await response.text();
};

const uploadSignedQuote = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", attachments.files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/uploads?id=${id}&filetype=signed_quote`,
        requestOptions,
    );
    return await response.text();
};

const uploadMesAtt = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", attachments.files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(`${hostedURL}/uploads?id=${id}&filetype=mes_att`, requestOptions);
    return await response.text();
};

const uploadOrderDetail = async (id) => {
    const token = await getToken();

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append("upload", quote_data.files[0]);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
    };

    const response = await fetch(
        `${hostedURL}/uploads?id=${id}&filetype=order_detail`,
        requestOptions,
    );
    return await response.text();
};

//Error Messages ==========================================================================
const hideErrorMessage = (type) => {
    let errorMessage = "";
    switch (type) {
        //Login
        case "login":
            errorMessage = document.getElementById("login-error");
            break;

        default:
            return;
    }
    errorMessage.style.display = "none";
};

const showErrorMessage = (type, message) => {
    let errorMessage = "";
    switch (type) {
        //Login
        case "login":
            errorMessage = document.getElementById("login-error");
            break;

        default:
            return;
    }
    errorMessage.style.display = "block";
    errorMessage.innerText = message;
};

//Other ==========================================================================
const squareIcon = '<i class="far fa-square"></i>';
const checkIcon = '<i class="far fa-check-square"></i>';

const updateCheckbox = (type) => {
    if (type === "terms_agreement") {
        if (acceptTerms === 1) {
            acceptTerms = 0;
            renderTermsAgreement();
        } else {
            acceptTerms = 1;
            renderTermsAgreement();
        }
    }
};

const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const getDate = () => {
    const currentdate = new Date();
    const datetime = `${
        currentdate.getMonth() + 1
    }/${currentdate.getDate()}/${currentdate.getFullYear()} ${currentdate.getHours()}:${currentdate.getMinutes()}:${currentdate.getSeconds()}`;
    return datetime;
};

const dwnld = (doc, record, number) => {
    if (doc === "ds_tag") {
        downloadRequest("api_prov_assets", record, "prov_tag_pdf", number);
    } else if (doc === "unsigned_quote") {
        const filename = `Quote_${number}.pdf`;
        downloadRequest("api_quote", record, "qte_quote_doc", filename);
    } else if (doc === "signed_quote") {
        const filename = `Signed_Quote_${number}.pdf`;
        downloadRequest("api_quote", record, "qte_quote_signed_doc", filename);
    } else if (doc === "terms") {
        const filename = `Terms_and_Conditions_Quote_${number}.pdf`;
        downloadRequest("api_quote", record, "qte_terms_doc", filename);
    } else if (doc === "supporting_docs") {
        const filename = `Supporting_Documents_Quote_${number}.zip`;
        downloadRequest("api_quote", record, "qte_supporting_doc", filename);
    } else if (doc === "eval_pdf") {
        const filename = `Evaluation_${number}.pdf`;
        downloadRequest("api_rec", record, "rec_pdf_report", filename);
    } else if (doc === "eval_ddc") {
        const filename = `Evaluation_Data_Destruction_Certificates_${number}.pdf`;
        downloadRequest("api_rec", record, "rec_bulk_ddc", filename);
    } else if (doc === "eval_ddc_external") {
        const filename = `Evaluation_Third_Party_Certificates_${number}.pdf`;
        downloadRequest("api_rec", record, "rec_third_party_ddc", filename);
    } else if (doc === "eval_xls") {
        const filename = `Evaluation_${number}.xlsx`;
        downloadRequest("api_rec", record, "rec_excel_report", filename);
    } else if (doc === "eval_supporting") {
        const filename = `Supporting_Documents_Evaluation_${number}.zip`;
        downloadRequest("api_rec", record, "rec_supporting_docs", filename);
    } else if (doc === "eval_images") {
        const filename = `Asset_Images_Evaluation_${number}.zip`;
        downloadRequest("api_rec", record, "rec_bulk_asset_images", filename);
    } else if (doc === "data_destruction") {
        const filename = `Data_Destruction_Certificate_${number}.pdf`;
        downloadRequest("api_rec_asset", record, "data_destruction", filename);
    } else if (doc === "order_detail_attachment") {
        downloadRequest("api_order_details", record, "det_attachment", number);
    } else if (doc === "mes_attachment") {
        downloadRequest("api_message", record, "mes_attachment", number);
    } else if (doc === "tablet") {
        const filename = `Tablets_Order_${number}.xlsx`;
        downloadRequest("api_order", record, "ord_tablet_spreadsheet", filename);
    } else if (doc === "desktop") {
        const filename = `Desktops_Order_${number}.xlsx`;
        downloadRequest("api_order", record, "ord_desktop_spreadsheet", filename);
    } else if (doc === "mobile") {
        const filename = `Mobile_Devices_Order_${number}.xlsx`;
        downloadRequest("api_order", record, "ord_mobile_spreadsheet", filename);
    } else if (doc === "laptop") {
        const filename = `Laptops_Order_${number}.xlsx`;
        downloadRequest("api_order", record, "ord_laptop_spreadsheet", filename);
    } else if (doc === "other") {
        const filename = `Other_Devices_Order_${number}.xlsx`;
        downloadRequest("api_order", record, "ord_other_spreadsheet", filename);
    } else if (doc === "mail_template") {
        const link = document.createElement("a");
        link.href = "./img/Addresses.xlsx";
        link.download = "Addresses.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else if (doc === "privacy_policy") {
        const link = document.createElement("a");
        link.href = "./img/CLARITY_Privacy_Policy_01_01_2026.pdf";
        link.download = "CLARITY_Privacy_Policy_01_01_2026.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else if (doc === "service_terms") {
        const link = document.createElement("a");
        link.href = "./img/CLARITY_Terms_of_Service_Agreement_01_01_2026.pdf";
        link.download = "CLARITY_Terms_of_Service_Agreement_01_01_2026.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else if (doc === "customer_reference") {
        const link = document.createElement("a");
        link.href = "./img/Customer_Reference_Info.pdf";
        link.download = "Customer_Reference_Info.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else if (doc === "asset_image") {
        return new Promise((resolve) => {
            const link = document.createElement("a");
            link.href = record;
            link.download = number;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            resolve();
        });
    } else if (doc === "axm_setup") {
        const link = document.createElement("a");
        link.href = "./img/Apple_Instructions-ABM-ASM.pdf";
        link.download = "Apple_Instructions-ABM-ASM.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else if (doc === "mdm_import_template") {
        const link = document.createElement("a");
        link.href = "./img/device_import_template.xlsx";
        link.download = "device_import_template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

const message = (employee) => {
    window.location.href = `./messages.html?id=${employee}`;
};

//Terms Agreement ==========================================================================
let acceptTerms = 0;
const renderTermsAgreement = () => {
    const checkbox = document.getElementById("terms-checkbox");
    const button = document.querySelector("button");
    if (acceptTerms === 1) {
        checkbox.innerHTML = checkIcon;
        checkbox.className = "checked";
        button.disabled = false;
    } else {
        checkbox.innerHTML = squareIcon;
        checkbox.className = "";
        button.disabled = true;
    }
};

const confirmTermsAgreement = async () => {
    const res = await sendRequest("confirm_terms", "");
    window.location.href = "./quote_list.html";
};

//Account Confirmation ==========================================================================
const renderAccountConfirmation = () => {
    const email = document.getElementById("email");
    const customer = document.getElementById("customer");
    const oldCustomer = document.getElementById("old-customer");
    email.innerHTML = userData.email;
    customer.innerHTML = userData.customer_temp;
    if (userData.customer) {
        oldCustomer.innerHTML = userData.customer;
    } else {
        const oldCustomerParent = document.querySelector(".hide-old-customer");
        oldCustomerParent.style.display = "none";
    }
};

const confirmAccount = async () => {
    const res = await sendRequest("confirm_account", "");
    const result = JSON.parse(res.scriptResult);
    if (result.error) {
        window.location.href = `./index.html?session=${encodeURI(result.error)}`;
    } else if (result.result === "success") {
        window.location.href = `./set_password.html?id=${id}`;
    }
};

//Account Request ======================================================================
const renderAccountRequest = () => {
    alertMessage.style.display = "none";
};
const startQuoteRequest = () => {
    const mdm_type = document.getElementById("mdm_type");
    let valid = true;
    const requiredFields = document.querySelectorAll("#account-info .required input");
    for (const field of requiredFields) {
        if (field.value === "") {
            field.className = "invalid";
            valid = false;
        }
    }
    if (mdm_int === 1 && mdm_type.value === "") {
        mdm_type.className = "invalid";
        valid = false;
    }
    if (valid) {
        const company = document.getElementById("company");
        const first = document.getElementById("first");
        const last = document.getElementById("last");
        const email = document.getElementById("email");
        const phone = document.getElementById("phone");
        const address = document.getElementById("address");
        const address2 = document.getElementById("address2");
        const city = document.getElementById("city");
        const state = document.getElementById("state");
        const zip = document.getElementById("zip");
        const referred = document.getElementById("referred");
        const accountInfo = {};
        accountInfo.customer = company.value;
        accountInfo.first = first.value;
        accountInfo.last = last.value;
        accountInfo.name = `${first.value} ${last.value}`;
        accountInfo.email = email.value;
        accountInfo.phone = phone.value;
        accountInfo.address = address.value;
        accountInfo.address2 = address2.value;
        accountInfo.city = city.value;
        accountInfo.state = state.value;
        accountInfo.zip = zip.value;
        accountInfo.referred = referred.value;
        accountInfo.mdm_type = mdm_type.value;
        accountInfo.mdm_int = mdm_int;
        const address2display = address2.value ? `${address2.value}, ` : "";
        accountInfo.address_line = `${address.value}, ${address2display}${city.value}, ${state.value} ${zip.value}`;
        myStorage.setItem("req_data", JSON.stringify(accountInfo));
        window.location.href = "./quote_request_new.html";
    } else {
        alertMessage.innerHTML = "Please complete all required fields.";
        alertMessage.style.display = "block";
    }
};
const cancelAccountRequest = () => {
    myStorage.removeItem("req_data");
    window.location.href = "./index.html";
};

//Partner Opportunity ======================================================================
const renderPartnerOpportunity = () => {
    alertMessage.style.display = "none";
};
const submitPartnerOpportunity = async () => {
    let valid = true;
    const requiredFields = document.querySelectorAll("#account-info .required input");
    for (const field of requiredFields) {
        if (field.value === "") {
            field.className = "invalid";
            valid = false;
        }
    }
    if (!valid) {
        alertMessage.innerHTML = "Please complete all required fields.";
        alertMessage.style.display = "block";
        return;
    }
    const company = document.getElementById("company");
    const website = document.getElementById("website");
    const contact = document.getElementById("contact");
    const title = document.getElementById("title");
    const c_email = document.getElementById("c_email");
    const c_phone = document.getElementById("c_phone");
    const notes = document.getElementById("opp_notes");
    const name = document.getElementById("name");
    const email = document.getElementById("email");
    const r_company = document.getElementById("r_company");
    const qty = document.getElementById("qty");
    const oppInfo = {};
    oppInfo.fieldData = {
        opp_company_name: company.value,
        opp_website: website.value,
        opp_name: contact.value,
        opp_title: title.value,
        opp_email: c_email.value,
        opp_phone: c_phone.value,
        opp_comments: notes.value,
        opp_referred_by: name.value,
        opp_referred_by_email: email.value,
        opp_referred_by_company: r_company.value,
        opp_device_qty: qty.value,
    };
    const res = await createPartnerOpp(JSON.stringify(oppInfo));
    const recordID = res.recordId;
    if (recordID) {
        const res = await sendRequest("partner_opportunity_v3", recordID);
        // console.log(res)
        window.location.href = "./partner_opportunity_submitted.html";
    }
};

const cancelPartnerOpportunity = () => {
    window.location.href = "./index.html";
};

//Quote List ======================================================================
const renderQuoteList = async () => {
    const res = await sendRequest("quote_list", "");
    // console.log(res)
    data = res.data;
    if (data.quote_list) {
        const list = document.getElementById("quote-list");
        for (const item of data.quote_list) {
            if (item.quote_status !== "Accepted") {
                const list_item_div = document.createElement("div");
                list_item_div.className = "list-item flex-row clickable";
                list_item_div.addEventListener("click", () => {
                    window.location.href = `./quote.html?id=${item.id}`;
                });
                list.appendChild(list_item_div);

                const col_1 = document.createElement("p");
                col_1.className = "list-col-1";
                col_1.innerText = item.quote_number;
                list_item_div.appendChild(col_1);

                const col_2 = document.createElement("div");
                col_2.className = "flex-column list-col-2";
                list_item_div.appendChild(col_2);

                const customer = document.createElement("p");
                customer.innerText = item.customer_name;
                col_2.appendChild(customer);

                if (item.sub_customer) {
                    const sub_customer = document.createElement("p");
                    sub_customer.innerText = item.sub_customer;
                    sub_customer.className = "small";
                    col_2.appendChild(sub_customer);
                }
                if (item.reference) {
                    const reference = document.createElement("p");
                    reference.innerText = `Reference # ${item.reference}`;
                    reference.className = "small";
                    col_2.appendChild(reference);
                }

                const col_3 = document.createElement("p");
                col_3.innerText = item.type;
                col_3.className = "list-col-3";
                list_item_div.appendChild(col_3);

                const col_4 = document.createElement("p");
                col_4.innerText = item.item_total;
                col_4.className = "list-col-4";
                list_item_div.appendChild(col_4);

                const col_5 = document.createElement("p");
                col_5.innerText = item.exp_date;
                col_5.className = "list-col-5";
                list_item_div.appendChild(col_5);

                const col_6 = document.createElement("p");
                col_6.innerText = item.quote_status;
                col_6.className = "list-col-6";
                list_item_div.appendChild(col_6);
            }
        }
    }
};

//Order List ======================================================================
const renderOrderList = async () => {
    const res = await sendRequest("order_list", "");
    data = res.data;
    if (data.order_list) {
        const list = document.getElementById("order-list");
        for (const item of data.order_list) {
            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row clickable";
            if (+item.is_parent === 1) {
                list_item_div.addEventListener("click", () => {
                    window.location.href = `./sub_order_list.html?id=${item.id}`;
                });
            } else {
                list_item_div.addEventListener("click", () => {
                    window.location.href = `./order.html?id=${item.id}`;
                });
            }
            list.appendChild(list_item_div);

            const col_1 = document.createElement("p");
            col_1.className = "list-col-1";
            col_1.innerText = item.order_number;
            list_item_div.appendChild(col_1);

            const col_2 = document.createElement("div");
            col_2.className = "flex-column list-col-2";
            list_item_div.appendChild(col_2);

            const customer = document.createElement("p");
            customer.innerText = item.customer_name;
            col_2.appendChild(customer);

            if (item.sub_customer) {
                const sub_customer = document.createElement("p");
                sub_customer.innerText = item.sub_customer;
                sub_customer.className = "small";
                col_2.appendChild(sub_customer);
            }
            if (item.reference) {
                const reference = document.createElement("p");
                reference.innerText = `Reference # ${item.reference}`;
                reference.className = "small";
                col_2.appendChild(reference);
            }

            const col_3 = document.createElement("p");
            col_3.innerText = item.type;
            col_3.className = "list-col-3";
            list_item_div.appendChild(col_3);

            const col_4 = document.createElement("p");
            col_4.innerText = item.item_total;
            col_4.className = "list-col-4";
            list_item_div.appendChild(col_4);

            const col_5 = document.createElement("p");
            col_5.innerText = item.status_date;
            col_5.className = "list-col-5";
            list_item_div.appendChild(col_5);

            const col_6 = document.createElement("p");
            col_6.innerText = item.order_status;
            col_6.className = "list-col-6";
            list_item_div.appendChild(col_6);
        }
    }
};

//Quote Detail ======================================================================
const renderQuote = async () => {
    const res = await sendRequest("quote_detail", `${id}`);
    data = res.data;

    const inked_signature_option = userData.inked_signature_option;
    const inked_sow_option = userData.inked_sow_option;
    const sign_quote_link = document.getElementById("signQuoteLink");
    const upload_quote_link = document.getElementById("uploadQuoteLink");
    const upload_sow_link = document.getElementById("uploadSOWLink");

    if (+inked_signature_option === 1 || +inked_sow_option === 1) {
        sign_quote_link.style.display = "none";
    }
    if (+inked_signature_option !== 1) {
        upload_quote_link.style.display = "none";
    }
    if (+inked_sow_option !== 1) {
        upload_sow_link.style.display = "none";
    }

    const showPricing = userData.show_pricing;
    const showDocs = userData.show_docs;
    const wrapper = document.querySelector("#wrapper");
    if (!showPricing) {
        wrapper.className = "flex-row hide-pricing";
    }
    if (!showDocs) {
        wrapper.className = "flex-row hide-docs";
    }
    if (!(showDocs || showPricing)) {
        wrapper.className = "flex-row hide-docs hide-pricing";
    }
    if (data.supporting_docs !== 1) {
        const supportingDocs = document.getElementById("supporting-docs");
        supportingDocs.style.display = "none";
    }
    // if (data.terms_doc != 1) {
    //   var termsDoc = document.getElementById('terms-doc');
    //   termsDoc.style.display = 'none';
    // }
    if (data.quote_status === "Expired") {
        sign_quote_link.style.display = "none";
    }

    buttonSubmit.disabled = true;
    buttonOptions.disabled = true;
    checkbox.innerHTML = squareIcon;
    checkbox.className = "";
    checkboxOne.innerHTML = squareIcon;
    checkboxOne.className = "";
    checkboxTwo.innerHTML = squareIcon;
    checkboxTwo.className = "";
    record = data.record;
    q_number = data.quote_number;

    for (const item of document.querySelectorAll(".quote-number")) {
        item.innerText = q_number;
    }

    //Quote Header
    const order = document.getElementById("order-header");
    const order_div = order.querySelector("div.inline-items");
    const customer = document.createElement("h1");
    customer.innerText = data.customer_name;
    order.insertBefore(customer, order_div);
    if (data.sub_customer && data.reference) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = `${data.sub_customer} - Reference # ${data.reference}`;
        order.insertBefore(sub_customer, order_div);
    } else if (data.sub_customer) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = data.sub_customer;
        order.insertBefore(sub_customer, order_div);
    } else if (data.reference) {
        const reference = document.createElement("h2");
        reference.innerText = `Reference # ${data.reference}`;
        order.insertBefore(reference, order_div);
    }
    const order_contact_info = document.createElement("div");
    order_contact_info.id = "order-contact-info";
    order.insertBefore(order_contact_info, order_div);
    const contact = document.createElement("p");
    contact.innerText = data.contact_name;
    contact.className = "bold";
    order_contact_info.appendChild(contact);
    const address = document.createElement("p");
    address.innerText = data.address_line;
    order_contact_info.appendChild(address);
    const email = document.createElement("p");
    email.innerText = data.email;
    order_contact_info.appendChild(email);
    const phone = document.createElement("p");
    phone.innerText = data.phone;
    order_contact_info.appendChild(phone);
    //Quote Summary
    if (data.amount_total) {
        const quote_total = document.getElementById("quote-total");
        const amount_total = document.createElement("p");
        amount_total.innerText = currency.format(data.amount_total);
        quote_total.appendChild(amount_total);

        if (data.min_value) {
            const min_value = document.createElement("p");
            min_value.className = "min_value";
            min_value.innerHTML = `Minimum Guaranteed Value: <span class='min_value_large'>${currency.format(data.min_value)}</span>`;
            quote_total.appendChild(min_value);
            const min_value_terms = document.createElement("p");
            min_value_terms.className = "min_value_terms";
            min_value_terms.innerText = "See Exhibit B for minimum guarantee terms and conditions.";
            quote_total.appendChild(min_value_terms);
        }
        if (data.terms) {
            const terms = document.createElement("p");
            terms.className = "terms";
            terms.innerHTML = data.terms;
            quote_total.appendChild(terms);
        }
        const device_total = document.getElementById("device-total");
        const item_total = document.createElement("p");
        item_total.innerText = data.item_total;
        device_total.appendChild(item_total);
        const device_breakdown = document.getElementById("device-total-breakdown");
        const tablet_total = document.createElement("p");
        tablet_total.innerText = data.tablet_total;
        device_breakdown.appendChild(tablet_total);
        const laptop_total = document.createElement("p");
        laptop_total.innerText = data.laptop_total;
        device_breakdown.appendChild(laptop_total);
        const desktop_total = document.createElement("p");
        desktop_total.innerText = data.desktop_total;
        device_breakdown.appendChild(desktop_total);
        const mobile_total = document.createElement("p");
        mobile_total.innerText = data.mobile_total;
        device_breakdown.appendChild(mobile_total);
        const other_total = document.createElement("p");
        other_total.innerText = data.other_total;
        device_breakdown.appendChild(other_total);
    } else {
        const quote_total = document.getElementById("product-summary");
        quote_total.style.display = "none";
    }

    if (data.services_total) {
        const services_total = document.getElementById("services-total");
        const s_amount_total = document.createElement("p");
        s_amount_total.innerText = currency.format(data.services_total);
        services_total.appendChild(s_amount_total);
    } else {
        const services_total = document.getElementById("services-summary");
        services_total.style.display = "none";
    }

    //Quote Items
    if (data.quote_items) {
        // console.log(data.quote_items)
        const list = document.getElementById("quote-item-list");

        for (const item of data.quote_items) {
            let group_div = document.getElementById(item.group_id_type);
            if (!group_div) {
                //Div for qlineitems
                group_div = document.createElement("div");
                group_div.id = item.group_id_type;

                //Code to render group headers and footer and list container
                const group_item_div = document.createElement("div");
                group_item_div.className = "quote-item-list";
                list.appendChild(group_item_div);

                const group_header = document.createElement("div");
                group_header.className = "group-list-header flex-row";
                group_item_div.appendChild(group_header);

                const group_header_text = document.createElement("h1");
                if (item.group_type === "Product" && item.group_id === "") {
                    group_header_text.innerText = "Product Upcycle";
                } else if (item.group_type === "Product") {
                    group_header_text.innerText = `Product Upcycle - ${item.group_name}`;
                } else if (item.group_type === "Service" && item.group_id === "") {
                    group_header_text.innerText = "Services";
                } else if (item.group_type === "Service") {
                    group_header_text.innerText = `Services - ${item.group_name}`;
                }
                group_header.appendChild(group_header_text);

                const list_header = document.createElement("div");
                list_header.className = "list-header flex-row";
                group_item_div.appendChild(list_header);

                const h_col_1 = document.createElement("h1");
                h_col_1.innerText = "Description";
                h_col_1.className = "list-col-1";
                list_header.appendChild(h_col_1);

                const h_col_2 = document.createElement("h1");
                h_col_2.innerText = "Quantity";
                h_col_2.className = "list-col-2";
                list_header.appendChild(h_col_2);

                const h_col_3 = document.createElement("h1");
                if (item.group_type === "Product") {
                    h_col_3.innerText = "Value";
                } else {
                    h_col_3.innerText = "Cost";
                }
                h_col_3.className = "list-col-3";
                list_header.appendChild(h_col_3);

                const h_col_4 = document.createElement("h1");
                if (item.group_type === "Product") {
                    h_col_4.innerText = "Total Value";
                } else {
                    h_col_4.innerText = "Total Cost";
                }
                h_col_4.className = "list-col-4";
                list_header.appendChild(h_col_4);

                //Squeeze in Qlineitems div
                group_item_div.appendChild(group_div);

                const group_footer = document.createElement("div");
                group_footer.className = "group-list-footer flex-row";
                group_item_div.appendChild(group_footer);
                const group_footer_1 = document.createElement("p");
                group_footer_1.innerText = `Total for ${group_header_text.innerText}`;
                group_footer.appendChild(group_footer_1);
                const group_footer_2 = document.createElement("h1");
                group_footer_2.innerText = currency.format(item.group_total);
                group_footer.appendChild(group_footer_2);
            }
            //Qlineitem
            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row";
            group_div.appendChild(list_item_div);
            const col_1 = document.createElement("div");
            col_1.className = "flex-column list-col-1";
            list_item_div.appendChild(col_1);
            const device_name = document.createElement("p");
            device_name.innerText = item.device_name;
            col_1.appendChild(device_name);
            const model = document.createElement("p");
            model.innerText = item.model;
            model.className = "small";
            col_1.appendChild(model);
            const col_2 = document.createElement("p");
            col_2.innerText = item.quantity;
            col_2.className = "list-col-2";
            list_item_div.appendChild(col_2);
            if (showPricing) {
                const col_3 = document.createElement("p");
                col_3.innerText = currency.format(item.price);
                col_3.className = "list-col-3";
                list_item_div.appendChild(col_3);
                const col_4 = document.createElement("p");
                col_4.innerText = currency.format(item.total);
                col_4.className = "list-col-4";
                list_item_div.appendChild(col_4);
            }
        }
    }

    //Quote Info
    const order_info = document.getElementById("order-info");
    const quote_number = document.createElement("h1");
    quote_number.innerHTML = `Quote <br/>${data.quote_number}`;
    order_info.appendChild(quote_number);
    const quote_status = document.createElement("h2");
    quote_status.innerText = data.quote_status;
    order_info.appendChild(quote_status);
    const quote_date = document.createElement("h3");
    quote_date.innerText = `Issued: ${data.quote_date}`;
    order_info.appendChild(quote_date);
    const exp_date = document.createElement("h3");
    exp_date.innerText = `Expires: ${data.exp_date}`;
    order_info.appendChild(exp_date);
    if (data.pickup_exp_date) {
        const pickup_exp_date = document.createElement("h3");
        pickup_exp_date.innerText = `Pickup/Mail-in Expiration: ${data.pickup_exp_date}`;
        order_info.appendChild(pickup_exp_date);
    }

    //Customer Service
    const cs_list = document.getElementById("customer-service-wrapper");
    for (const item of data.customer_service) {
        const cs_div = document.createElement("div");
        cs_div.className = "customer-service";
        cs_list.appendChild(cs_div);
        const avatar = document.createElement("div");
        avatar.className = "avatar";
        avatar.innerHTML = item.img;
        cs_div.appendChild(avatar);
        const name = document.createElement("h2");
        name.innerText = item.name;
        cs_div.appendChild(name);
        const title = document.createElement("p");
        title.innerText = item.title;
        cs_div.appendChild(title);
        // if (item.terminated != 1) {
        //     var message = document.createElement("p");
        //     message.innerHTML = item.link;
        //     cs_div.appendChild(message);
        // }
    }
};

//Order Detail ======================================================================
const renderOrder = async () => {
    const res = await sendRequest("order_detail_v2", `${id}`);

    data = res.data;

    //Hide pricing if needed
    const isAdmin = userData.is_admin;
    const restrictedUser = userData.restricted_user;
    // `data.guest_access` is set whenever the *current* user is themselves a guest
    // on this order — so an admin who is also a guest must still see guest
    // management. Only hide it from non-admin guests (and non-admin/non-restricted
    // users generally).
    const guestUser = data.guest_access;
    const guestAccess = document.querySelector("#order-header > .inline-items");
    if (!(restrictedUser || isAdmin) || (guestUser && !isAdmin)) {
        guestAccess.style.display = "none";
    }

    buttonSubmit.disabled = true;
    checkbox.innerHTML = squareIcon;
    checkbox.className = "";
    record = data.record;
    quote_record = data.quote_record;
    o_number = data.order_number;

    //Order Header
    const order = document.getElementById("order-header");
    const order_div = order.querySelector("div.inline-items");
    if (+data.is_child === 1) {
        const customerNameDiv = document.createElement("div");
        customerNameDiv.id = "customer-name";
        customerNameDiv.className = "inline-items";
        const backButton = document.createElement("p");
        backButton.className = "link";
        backButton.innerHTML = '<i class="fas fa-chevron-left"></i>';
        backButton.addEventListener("click", () => {
            window.location.href = `./sub_order_list.html?id=${data.id_parent}`;
        });
        customerNameDiv.appendChild(backButton);
        const customer = document.createElement("h1");
        customer.innerText = data.customer_name;
        customerNameDiv.appendChild(customer);
        order.insertBefore(customerNameDiv, order_div);
    } else {
        const customer = document.createElement("h1");
        customer.innerText = data.customer_name;
        order.insertBefore(customer, order_div);
    }

    if (data.sub_customer && data.reference) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = `${data.sub_customer} - Reference # ${data.reference}`;
        order.insertBefore(sub_customer, order_div);
    } else if (data.sub_customer) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = data.sub_customer;
        order.insertBefore(sub_customer, order_div);
    } else if (data.reference) {
        const reference = document.createElement("h2");
        reference.innerText = `Reference # ${data.reference}`;
        order.insertBefore(reference, order_div);
    }
    const order_contact_info = document.createElement("div");
    order_contact_info.id = "order-contact-info";
    order.insertBefore(order_contact_info, order_div);
    const contact = document.createElement("p");
    contact.innerText = data.contact_name;
    contact.className = "bold";
    order_contact_info.appendChild(contact);
    const address = document.createElement("p");
    address.innerText = data.address_line;
    order_contact_info.appendChild(address);
    const email = document.createElement("p");
    email.innerText = data.email;
    order_contact_info.appendChild(email);
    const phone = document.createElement("p");
    phone.innerText = data.phone;
    order_contact_info.appendChild(phone);
    //Order Info
    const order_info = document.getElementById("order-info");
    const order_number = document.createElement("h1");
    order_number.innerText = `Order ${data.order_number}`;
    order_info.appendChild(order_number);
    const order_status = document.createElement("h2");
    order_status.innerText = data.order_status;
    order_info.appendChild(order_status);
    const status_date = document.createElement("h3");
    status_date.innerText = data.status_date;
    order_info.appendChild(status_date);
    //Timeline
    const timeline = document.getElementById("timeline-items");
    if (data.timeline) {
        timeline.innerHTML = data.timeline;
    }
    // Customer Service
    const cs_list = document.getElementById("customer-service-wrapper");
    if (data.customer_service) {
        for (const item of data.customer_service) {
            const cs_div = document.createElement("div");
            cs_div.className = "customer-service";
            cs_list.appendChild(cs_div);
            const avatar = document.createElement("div");
            avatar.className = "avatar";
            avatar.innerHTML = item.img;
            cs_div.appendChild(avatar);
            const name = document.createElement("h2");
            name.innerText = item.name;
            cs_div.appendChild(name);
            const title = document.createElement("p");
            title.innerText = item.title;
            cs_div.appendChild(title);
            // if (item.terminated != 1) {
            //     var message = document.createElement("p");
            //     message.innerHTML = item.link;
            //     cs_div.appendChild(message);
            // }
        }
    }
    addCallTS = document.querySelector("div.addCallTS");
    callStatus = document.getElementById("callStatus");
    scheduleLink = document.getElementById("scheduleLink");
    dateTime = document.getElementById("dateTime");
    renderFileUploaded();

    const surveyComplete = data.survey_complete;
    const surveyReady = data.survey_ready;
    if (
        (data.order_status === "Payment Sent" ||
            data.order_status === "Evaluation Summary" ||
            surveyReady) &&
        !surveyComplete
    ) {
        modalSurvey.style.display = "block";
    }
    const paymentConfirmed = +data.payment_confirmed === 1;
    if (data.order_status !== "Payment Sent" && !paymentConfirmed) {
        openModal("paymentConfirm");
    } else if (paymentConfirmed) {
        paymentLink.innerHTML = "Confirmed Payment Details";
        paymentModalVerbiage.innerHTML = "Your payment details have been confirmed.";
    }
};

//Function to dynamically render the timeline graphics to align with timeline items
const renderGraphics = () => {
    const incompleteIcon = '<i class="fas fa-circle"></i>';
    const activeIcon = '<i class="fas fa-dot-circle"></i>';
    const completedIcon = '<i class="fas fa-check-circle"></i>';
    const items = document.querySelectorAll(".item");
    const graphicsContainer = document.getElementById("timeline-graphics");
    graphicsContainer.innerText = "";
    items.forEach((item, index) => {
        //render circles
        const icon = document.createElement("div");
        if (item.className === "item active") {
            icon.innerHTML = activeIcon;
            icon.className = "active";
        } else if (item.className === "item pending") {
            icon.innerHTML = incompleteIcon;
            icon.className = "pending";
        } else {
            icon.innerHTML = completedIcon;
            icon.className = "complete";
        }
        graphicsContainer.appendChild(icon);

        //render lines
        if (items.length - 1 !== index) {
            const line = document.createElement("div");
            line.className = "vertical-line";
            graphicsContainer.appendChild(line);
            line.style.height = `${item.offsetHeight - 40}px`;
        }
    });
    // Scroll automatically to the active timeline item
    const content = document.getElementById("timeline-wrapper");
    const activeItem = document.getElementById("active");
    if (activeItem) {
        const scrollPosition = activeItem.offsetTop - 275;

        content.scroll({
            top: scrollPosition,
            left: 0,
            behavior: "smooth",
        });
    }
};

//Sub Order List ======================================================================

const renderSubOrderList = async () => {
    const res = await sendRequest("sub_order_list", `${id}`);

    data = res.data;
    record = data.record;
    quote_record = data.quote_record;
    o_number = data.order_number;

    const evalLink = document.getElementById("evalLink");
    if (data.has_rec !== 1) {
        evalLink.style.display = "none";
    }

    //Order Header
    const order = document.getElementById("order-header");
    const order_div = order.querySelector("div.inline-items");
    const order_number = document.createElement("h2");
    order_number.innerText = `Order ${data.order_number}`;
    order.insertBefore(order_number, order_div);
    const customer = document.createElement("h1");
    customer.innerText = data.customer_name;
    order.insertBefore(customer, order_div);
    if (data.sub_customer && data.reference) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = `${data.sub_customer} - Reference # ${data.reference}`;
        order.insertBefore(sub_customer, order_div);
    } else if (data.sub_customer) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = data.sub_customer;
        order.insertBefore(sub_customer, order_div);
    } else if (data.reference) {
        const reference = document.createElement("h2");
        reference.innerText = `Reference # ${data.reference}`;
        order.insertBefore(reference, order_div);
    }
    const order_contact_info = document.createElement("div");
    order_contact_info.id = "order-contact-info";
    order.insertBefore(order_contact_info, order_div);
    const contact = document.createElement("p");
    contact.innerText = data.contact_name;
    contact.className = "bold";
    order_contact_info.appendChild(contact);
    const address = document.createElement("p");
    address.innerText = data.address_line;
    order_contact_info.appendChild(address);
    const email = document.createElement("p");
    email.innerText = data.email;
    order_contact_info.appendChild(email);
    const phone = document.createElement("p");
    phone.innerText = data.phone;
    order_contact_info.appendChild(phone);

    //Order List
    if (data.order_list) {
        const list = document.getElementById("order-list");
        for (const item of data.order_list) {
            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row clickable";
            list_item_div.addEventListener("click", () => {
                window.location.href = `./order.html?id=${item.id}`;
            });
            list.appendChild(list_item_div);

            const col_1 = document.createElement("p");
            col_1.className = "list-col-1";
            col_1.innerText = item.order_number;
            list_item_div.appendChild(col_1);

            const col_2 = document.createElement("div");
            col_2.className = "flex-column list-col-2";
            list_item_div.appendChild(col_2);

            const customer = document.createElement("p");
            customer.innerText = item.customer_name;
            col_2.appendChild(customer);

            if (item.sub_customer) {
                const sub_customer = document.createElement("p");
                sub_customer.innerText = item.sub_customer;
                sub_customer.className = "small";
                col_2.appendChild(sub_customer);
            }
            if (item.reference) {
                const reference = document.createElement("p");
                reference.innerText = `Reference # ${item.reference}`;
                reference.className = "small";
                col_2.appendChild(reference);
            }

            const col_3 = document.createElement("p");
            col_3.innerText = item.type;
            col_3.className = "list-col-3";
            list_item_div.appendChild(col_3);

            const col_4 = document.createElement("p");
            col_4.innerText = item.item_total;
            col_4.className = "list-col-4";
            list_item_div.appendChild(col_4);

            const col_5 = document.createElement("p");
            col_5.innerText = item.status_date;
            col_5.className = "list-col-5";
            list_item_div.appendChild(col_5);

            const col_6 = document.createElement("p");
            col_6.innerText = item.order_status;
            col_6.className = "list-col-6";
            list_item_div.appendChild(col_6);
        }
    }
};

//Evaluation Summary ======================================================================
const renderEval = async () => {
    // const recData = myStorage.getItem(id);
    // data = JSON.parse(recData);

    // if (!data) {
    //     const res = await sendRequest("eval_summary", `${id}`);
    //     data = res.data;
    //     myStorage.setItem(id, JSON.stringify(data));
    // }

    const res = await sendRequest("eval_summary", `${id}`);
    data = res.data;
    myStorage.setItem(id, JSON.stringify(data));

    eval_record = data.rec.record;
    eval_number = data.order_number;

    const showPricing = userData.show_pricing;
    const showDocs = userData.show_docs;
    const wrapper = document.querySelector("#wrapper");
    let accessories = document.querySelector("#hide");
    if (!showPricing) {
        wrapper.className = "flex-row hide-pricing";
        accessories = document.querySelector("#show");
    }
    if (!showDocs) {
        wrapper.className = "flex-row hide-docs";
    }
    if (!(showDocs || showPricing)) {
        wrapper.className = "flex-row hide-docs hide-pricing";
        accessories = document.querySelector("#show");
    }
    accessories.className = "list-header flex-row hide";

    //Hide Download Links if the files aren't in Clarity
    if (data.rec.eval_pdf !== "1") {
        const evalPDF = document.getElementById("eval-pdf");
        evalPDF.style.display = "none";
    }
    if (data.rec.eval_excel !== "1") {
        const evalExcel = document.getElementById("eval-excel");
        evalExcel.style.display = "none";
    }
    if (data.rec.eval_ddc !== "1") {
        const evalDDC = document.getElementById("eval-ddc");
        evalDDC.style.display = "none";
    }
    if (data.rec.eval_ddc_external !== "1") {
        const evalDDCExternal = document.getElementById("eval-ddc-external");
        evalDDCExternal.style.display = "none";
    }
    if (data.rec.eval_supporting !== "1") {
        const evalDocs = document.getElementById("eval-docs");
        evalDocs.style.display = "none";
    }
    if (data.rec.eval_images !== "1") {
        const evalImages = document.getElementById("eval-images");
        evalImages.style.display = "none";
    }

    const customer_header = document.getElementById("customer-name");
    const order = document.getElementById("order-header");
    const order_div = order.querySelector("#docs");
    const customer = document.createElement("h1");
    customer.innerText = data.customer_name;
    customer_header.appendChild(customer);
    // order.insertBefore(customer, order_div);
    if (data.sub_customer && data.reference) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = `${data.sub_customer} - Reference # ${data.reference}`;
        order.insertBefore(sub_customer, order_div);
    } else if (data.sub_customer) {
        const sub_customer = document.createElement("h2");
        sub_customer.innerText = data.sub_customer;
        order.insertBefore(sub_customer, order_div);
    } else if (data.reference) {
        const reference = document.createElement("h2");
        reference.innerText = `Reference # ${data.reference}`;
        order.insertBefore(reference, order_div);
    }
    const order_contact_info = document.createElement("div");
    order_contact_info.id = "order-contact-info";
    order.insertBefore(order_contact_info, order_div);
    const contact = document.createElement("p");
    contact.innerText = data.contact_name;
    contact.className = "bold";
    order_contact_info.appendChild(contact);
    const address = document.createElement("p");
    address.innerText = data.address_line;
    order_contact_info.appendChild(address);
    const email = document.createElement("p");
    email.innerText = data.email;
    order_contact_info.appendChild(email);
    const phone = document.createElement("p");
    phone.innerText = data.phone;
    order_contact_info.appendChild(phone);
    //Quote Summary
    if (showPricing) {
        const pricing_totals = document.getElementById("pricing-totals");
        const quote_total = document.getElementById("quote-total");
        const amount_total = document.createElement("p");
        amount_total.innerText = currency.format(data.rec.payout_total);
        quote_total.insertBefore(amount_total, pricing_totals);
        pricing_totals.innerHTML = data.rec.pricing_html;
    }
    const grade_totals = document.getElementById("grade-totals");
    grade_totals.innerHTML = data.rec.grades_html;
    const device_total = document.getElementById("device-total");
    const item_total = document.createElement("p");
    item_total.innerText = data.rec.rec_item_total;
    device_total.insertBefore(item_total, grade_totals);
    const device_breakdown = document.getElementById("device-total-breakdown");
    const tablet_total = document.createElement("p");
    tablet_total.innerText = data.rec.rec_tablet_total;
    device_breakdown.appendChild(tablet_total);
    const laptop_total = document.createElement("p");
    laptop_total.innerText = data.rec.rec_laptop_total;
    device_breakdown.appendChild(laptop_total);
    const desktop_total = document.createElement("p");
    desktop_total.innerText = data.rec.rec_desktop_total;
    device_breakdown.appendChild(desktop_total);
    const mobile_total = document.createElement("p");
    mobile_total.innerText = data.rec.rec_mobile_total;
    device_breakdown.appendChild(mobile_total);
    const other_total = document.createElement("p");
    other_total.innerText = data.rec.rec_other_total;
    device_breakdown.appendChild(other_total);
    //Device Summary Items
    const device_list = document.getElementById("device-summary-list");
    if (data.rec.device_list) {
        for (const item of data.rec.device_list) {
            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row";
            device_list.appendChild(list_item_div);

            const col_1 = document.createElement("div");
            col_1.className = "flex-column list-col-1";
            list_item_div.appendChild(col_1);

            const device_name = document.createElement("p");
            device_name.innerText = item.device_name;
            col_1.appendChild(device_name);

            const model = document.createElement("p");
            model.innerText = item.model;
            model.className = "small";
            col_1.appendChild(model);

            const col_2 = document.createElement("p");
            col_2.innerText = item.quantity;
            col_2.className = "list-col-2";
            list_item_div.appendChild(col_2);

            if (showPricing) {
                const col_3 = document.createElement("p");
                col_3.innerText = currency.format(item.price);
                col_3.className = "list-col-3";
                list_item_div.appendChild(col_3);

                const col_4 = document.createElement("p");
                col_4.innerText = currency.format(item.total);
                col_4.className = "list-col-4";
                list_item_div.appendChild(col_4);

                const col_5 = document.createElement("p");
                col_5.innerText = currency.format(item.graded_total);
                col_5.className = "list-col-5";
                list_item_div.appendChild(col_5);
            }
        }
    }
    //Accessories
    const accessory_list = document.getElementById("accessory-list");
    if (data.rec.accessory_list) {
        for (const item of data.rec.accessory_list) {
            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row";
            accessory_list.appendChild(list_item_div);
            const col_1 = document.createElement("p");
            col_1.innerText = item.name;
            col_1.className = "list-col-1";
            list_item_div.appendChild(col_1);
            if (showPricing) {
                const col_2 = document.createElement("p");
                col_2.innerText = currency.format(item.price);
                col_2.className = "list-col-2";
                list_item_div.appendChild(col_2);
            }
            const col_3 = document.createElement("p");
            col_3.innerText = item.quantity;
            col_3.className = showPricing ? "list-col-3" : "list-col-2";
            list_item_div.appendChild(col_3);
            if (showPricing) {
                const col_4 = document.createElement("p");
                col_4.innerText = currency.format(item.total);
                col_4.className = "list-col-4";
                list_item_div.appendChild(col_4);
            }
        }
    }
    //Credits and Deductions
    if (showPricing) {
        const credit_list = document.getElementById("credits-deductions-list");
        if (data.rec.credit_list) {
            for (const item of data.rec.credit_list) {
                const list_item_div = document.createElement("div");
                list_item_div.className = "list-item flex-row";
                credit_list.appendChild(list_item_div);
                const col_1 = document.createElement("p");
                col_1.innerText = item.type;
                col_1.className = "list-col-1";
                list_item_div.appendChild(col_1);
                const col_2 = document.createElement("p");
                col_2.innerText = item.reason;
                col_2.className = "list-col-2";
                list_item_div.appendChild(col_2);
                const col_3 = document.createElement("p");
                col_3.innerText = currency.format(item.value);
                col_3.className = "list-col-3";
                list_item_div.appendChild(col_3);
            }
        }
    }

    //Order Info
    const order_info = document.getElementById("order-info");
    const order_number = document.createElement("h1");
    order_number.innerText = `Order ${data.order_number}`;
    order_info.appendChild(order_number);
    const rec_status = document.createElement("h2");
    rec_status.innerText = "Evaluation Summary";
    order_info.appendChild(rec_status);
    const rec_date = document.createElement("h3");
    rec_date.innerText = data.rec.report_date;
    order_info.appendChild(rec_date);
    //Customer Service
    const cs_list = document.getElementById("customer-service-wrapper");
    if (data.customer_service) {
        for (const item of data.customer_service) {
            const cs_div = document.createElement("div");
            cs_div.className = "customer-service";
            cs_list.appendChild(cs_div);
            const avatar = document.createElement("div");
            avatar.className = "avatar";
            avatar.innerHTML = item.img;
            cs_div.appendChild(avatar);
            const name = document.createElement("h2");
            name.innerText = item.name;
            cs_div.appendChild(name);
            const title = document.createElement("p");
            title.innerText = item.title;
            cs_div.appendChild(title);
            // if (item.terminated != 1) {
            //     var message = document.createElement("p");
            //     message.innerHTML = item.link;
            //     cs_div.appendChild(message);
            // }
        }
    }
};

//Evaluation Detail ======================================================================
const renderEvalDetail = async () => {
    const loaded = document.getElementById("loaded-list");
    const loading = document.getElementById("loader");
    const loadingMessage = document.getElementById("loadingMessage");
    loaded.style.display = "none";
    searchInput.disabled = "true";

    const recData = myStorage.getItem(id);
    data = JSON.parse(recData);
    // console.log(data)
    is_parent = data.is_parent;

    showPricing = data.guest_access ? data.guest_access.show_pricing : userData.show_pricing;
    showPhotos = data.guest_access ? data.guest_access.show_photos : userData.show_photos;
    image_option = userData.org_image_option;

    const wrapper = document.getElementById("wrapper");
    if (showPricing) {
        header = document.getElementById("hide-pricing");
    } else {
        header = document.getElementById("show-pricing");
        wrapper.className = "hide-pricing flex-row";
    }
    header.className = "list-header flex-row hide";

    if (+image_option !== 1 || +showPhotos !== 1) {
        assetImages.style.display = "none";
        downloadImages.style.display = "none";
    }

    //Order Info
    const order_info = document.getElementById("order-info");
    const order_number = document.createElement("h1");
    order_number.innerText = `Order ${data.order_number}`;
    order_info.appendChild(order_number);
    const rec_status = document.createElement("h2");
    rec_status.innerText = "Evaluation Summary";
    order_info.appendChild(rec_status);
    const rec_date = document.createElement("h3");
    rec_date.innerText = data.rec.report_date;
    order_info.appendChild(rec_date);
    //Customer Service
    const cs_list = document.getElementById("customer-service-wrapper");
    if (data.customer_service) {
        for (const item of data.customer_service) {
            const cs_div = document.createElement("div");
            cs_div.className = "customer-service";
            cs_list.appendChild(cs_div);
            const avatar = document.createElement("div");
            avatar.className = "avatar";
            avatar.innerHTML = item.img;
            cs_div.appendChild(avatar);
            const name = document.createElement("h2");
            name.innerText = item.name;
            cs_div.appendChild(name);
            const title = document.createElement("p");
            title.innerText = item.title;
            cs_div.appendChild(title);
            // if (item.terminated != 1) {
            //     var message = document.createElement("p");
            //     message.innerHTML = item.link;
            //     cs_div.appendChild(message);
            // }
        }
    }

    if (!data.rec.asset_list) {
        let assetList = [];
        let recordCount = 100;
        let count = 0;
        let res = {};
        while (count < recordCount) {
            const offset = count + 1;
            // `id` is the order id this page was opened with. The server derives
            // the reconciliation (and the parent/child branch this used to make
            // here) from it, so it no longer accepts a reconciliation or quote id.
            res = await getAssets(id, offset);
            count += res.dataInfo.returnedCount;
            recordCount = res.dataInfo.foundCount;
            const arrayOne = assetList.slice();
            const arrayTwo = res.data;
            assetList = arrayOne.concat(arrayTwo);
            loadingMessage.innerHTML = `Loading ${count} of ${recordCount} devices...`;
            const percent = (count / recordCount) * 100;
            const progress = document.querySelector(".meter span");
            progress.style.width = `${Math.floor(percent)}%`;
        }
        data.rec.asset_list = assetList;
    }
    searchInput.disabled = false;
    sorted_array = data.rec.asset_list.slice();
    sort("model");
};

const renderAssets = (item_data, sort, start, end) => {
    const loaded = document.getElementById("loaded-list");
    const loading = document.getElementById("loader");
    const pagination = document.querySelector(".pagination");
    const foundCountOnly = document.getElementById("foundCountOnly");
    const foundCount = document.getElementById("foundCount");
    const first = start + 1;
    const last = end > item_data.length ? item_data.length : end;
    const found = item_data.length < data.rec.rec_item_total ? " Found" : "";

    if (item_data.length <= 100) {
        pagination.style.display = "none";
        foundCountOnly.style.display = "";
        foundCountOnly.innerText = `Showing ${first}-${last} of ${item_data.length}${found}`;
    } else {
        pagination.style.display = "";
        foundCountOnly.style.display = "none";
        foundCount.innerText = `Showing ${first}-${last} of ${item_data.length}${found}`;
    }

    //Devices
    let model = "";
    let grade = "";
    let id = "";
    const array = item_data.slice(start, end);

    const device_list = document.querySelector("#asset-list");
    device_list.innerText = "";
    array.forEach((item_data, index) => {
        const item = JSON.parse(item_data.fieldData.json_data);

        //Render SubSummaries
        if (sort === "model" && model !== item.model) {
            //Render Model Summary
            model = item.model;
            const sub_div_1 = document.createElement("div");
            sub_div_1.className = "list-sub-summary-1 flex-row";
            device_list.appendChild(sub_div_1);

            const col_1 = document.createElement("h1");
            col_1.innerText = item.description;
            col_1.className = "list-col-1";
            sub_div_1.appendChild(col_1);

            const col_2 = document.createElement("h1");
            if (+is_parent === 1) {
                col_2.innerText = `Total Devices: ${item.parent_model_count}`;
            } else {
                col_2.innerText = `Total Devices: ${item.model_count}`;
            }
            col_2.className = "list-col-2";
            sub_div_1.appendChild(col_2);

            if (showPricing) {
                const col_3 = document.createElement("h1");
                if (+is_parent === 1) {
                    col_3.innerText = currency.format(item.parent_model_total);
                } else {
                    col_3.innerText = currency.format(item.model_total);
                }
                col_3.className = "list-col-3";
                sub_div_1.appendChild(col_3);
            }

            //Render Grade Summary
            grade = item.grade;
            if (grade !== "N/A") {
                const sub_div_2 = document.createElement("div");
                sub_div_2.className = "list-sub-summary-2 flex-row";
                device_list.appendChild(sub_div_2);

                const col_2_1 = document.createElement("h1");
                col_2_1.innerText = `Grade: ${item.grade}`;
                col_2_1.className = "list-col-1";
                sub_div_2.appendChild(col_2_1);

                const col_2_2 = document.createElement("h1");
                if (+is_parent === 1) {
                    col_2_2.innerText = `Total Devices: ${item.parent_model_grade_count}`;
                } else {
                    col_2_2.innerText = `Total Devices: ${item.model_grade_count}`;
                }
                col_2_2.className = "list-col-2";
                sub_div_2.appendChild(col_2_2);

                if (showPricing) {
                    const col_3 = document.createElement("h1");
                    if (+is_parent === 1) {
                        col_3.innerText = currency.format(item.parent_model_grade_total);
                    } else {
                        col_3.innerText = currency.format(item.model_grade_total);
                    }
                    col_3.className = "list-col-3";
                    sub_div_2.appendChild(col_3);
                }
            }

            if (id !== "") {
                const prevAsset = document.getElementById(id);
                prevAsset.style.border = "none";
            }
        } else if (sort === "model" && grade !== item.grade) {
            //Render Grade Summary
            grade = item.grade;
            const sub_div_2 = document.createElement("div");
            sub_div_2.className = "list-sub-summary-2 flex-row";
            device_list.appendChild(sub_div_2);

            const col_1 = document.createElement("h1");
            col_1.innerText = `Grade: ${item.grade}`;
            col_1.className = "list-col-1";
            sub_div_2.appendChild(col_1);

            const col_2 = document.createElement("h1");
            if (+is_parent === 1) {
                col_2.innerText = `Total Devices: ${item.parent_model_grade_count}`;
            } else {
                col_2.innerText = `Total Devices: ${item.model_grade_count}`;
            }
            col_2.className = "list-col-2";
            sub_div_2.appendChild(col_2);

            if (showPricing) {
                const col_3 = document.createElement("h1");
                if (+is_parent === 1) {
                    col_3.innerText = currency.format(item.parent_model_grade_total);
                } else {
                    col_3.innerText = currency.format(item.model_grade_total);
                }
                col_3.className = "list-col-3";
                sub_div_2.appendChild(col_3);
            }

            if (id !== "") {
                const prevAsset = document.getElementById(id);
                prevAsset.style.border = "none";
            }
        } else if (sort === "grade" && grade !== item.grade) {
            //Render Grade Summary
            grade = item.grade;
            const sub_div_1 = document.createElement("div");
            sub_div_1.className = "list-sub-summary-1 flex-row";
            device_list.appendChild(sub_div_1);

            const col_1 = document.createElement("h1");
            col_1.innerText = `Grade: ${item.grade}`;
            col_1.className = "list-col-1";
            sub_div_1.appendChild(col_1);

            const col_2 = document.createElement("h1");
            if (+is_parent === 1) {
                col_2.innerText = `Total Devices: ${item.parent_grade_count}`;
            } else {
                col_2.innerText = `Total Devices: ${item.grade_count}`;
            }
            col_2.className = "list-col-2";
            sub_div_1.appendChild(col_2);

            if (showPricing) {
                const col_3 = document.createElement("h1");
                if (+is_parent === 1) {
                    col_3.innerText = currency.format(item.parent_grade_total);
                } else {
                    col_3.innerText = currency.format(item.grade_total);
                }
                col_3.className = "list-col-3";
                sub_div_1.appendChild(col_3);
            }

            if (id !== "") {
                const prevAsset = document.getElementById(id);
                prevAsset.style.border = "none";
            }
        }

        //Render Asset
        id = item.id;
        const list_item_div = document.createElement("div");
        list_item_div.className = "list-item flex-row";
        list_item_div.id = item.id;
        device_list.appendChild(list_item_div);

        const col_1 = document.createElement("p");
        col_1.innerText = item.model;
        col_1.className = "list-col-1";
        list_item_div.appendChild(col_1);

        const col_2 = document.createElement("p");
        col_2.innerText = item.serial;
        col_2.className = "list-col-2";
        list_item_div.appendChild(col_2);

        if (showPricing) {
            const col_3 = document.createElement("p");
            col_3.innerText = currency.format(item.price);
            col_3.className = "list-col-3";
            list_item_div.appendChild(col_3);

            const col_4 = document.createElement("p");
            col_4.innerText = item.grade;
            col_4.className = "list-col-4";
            list_item_div.appendChild(col_4);
        }

        const col_5 = document.createElement("p");
        col_5.innerHTML =
            +data.rec.show_details === 1 && showPhotos && item.image && +image_option === 1
                ? imgIcon
                : "";
        col_5.className = showPricing ? "list-col-5" : "list-col-3";
        list_item_div.appendChild(col_5);

        const col_6 = document.createElement("p");
        if (+data.rec.show_details === 1 /* && item.grade != "N/A" */) {
            col_6.className = showPricing ? "link list-col-6" : "link list-col-4";
            col_6.innerText = "Details";
            col_6.addEventListener("click", () => {
                viewDetails(item.id, start + index);
            });
        } else {
            col_6.className = showPricing ? "list-col-6" : "list-col-4";
        }
        list_item_div.appendChild(col_6);

        if (showPricing) {
            const col_7 = document.createElement("p");
            col_7.innerText = currency.format(item.payout);
            col_7.className = "list-col-7";
            list_item_div.appendChild(col_7);
        } else {
            const col_4 = document.createElement("p");
            col_4.innerText = item.grade;
            col_4.className = "list-col-5";
            list_item_div.appendChild(col_4);
        }
    });

    loading.style.display = "none";
    loaded.style.display = "";
};

//Guest Access ======================================================================
const renderGuestAccess = async () => {
    const res = await sendRequest("guest_access", `${id}`);

    data = res.data;

    image_option = userData.org_image_option;
    if (+image_option === 1) {
        const header = document.querySelector(".list-col-2");
        const imageHeader = document.createElement("h1");
        imageHeader.innerHTML = `${picsIcon} Access to Photos`;
        header.appendChild(imageHeader);
    } else {
        hideImageOpt.style.display = "none";
    }

    const order_number = document.getElementById("order-number");
    order_number.innerText = `Order ${data.order_number} Guest Access`;

    renderGuestList();
};

const renderGuestList = () => {
    const list = document.getElementById("contact-list");
    list.innerHTML = "";
    const adminAccess = userData.is_admin;

    if (adminAccess && data.guest_contacts) {
        data.guest_contacts.forEach((item, index) => {
            let docs = "";
            let pricing = "";
            let pics = "";
            if (item.show_docs) {
                docs = docsIcon;
            }
            if (item.show_pricing) {
                pricing = dollarIcon;
            }
            if (item.show_photos && +image_option === 1) {
                pics = picsIcon;
            }

            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row";
            list.appendChild(list_item_div);

            const col_1 = document.createElement("p");
            col_1.className = "list-col-1";
            col_1.innerText = item.name;
            list_item_div.appendChild(col_1);

            const col_2 = document.createElement("p");
            col_2.innerText = item.title;
            col_2.className = "flex-column list-col-2";
            list_item_div.appendChild(col_2);

            const col_3 = document.createElement("p");
            const icons = docs + pricing + pics;

            col_3.innerHTML = icons;
            col_3.className = "list-col-3 flex-row";
            list_item_div.appendChild(col_3);

            const col_4 = document.createElement("p");
            col_4.innerText = "Remove";
            col_4.className = "list-col-4 link";
            col_4.addEventListener("click", () => {
                remove_org_contact(item.id, index);
            });
            list_item_div.appendChild(col_4);

            const col_5 = document.createElement("p");
            col_5.innerText = "Edit";
            col_5.className = "list-col-5 link";
            col_5.addEventListener("click", () => {
                edit_org_contact(item.id, index);
            });
            list_item_div.appendChild(col_5);
        });
    } else if (!adminAccess) {
        const list_item_div = document.createElement("div");
        list_item_div.className = "list-item flex-row";
        list.appendChild(list_item_div);

        const col_1 = document.createElement("p");
        col_1.className = "list-col-1";
        col_1.innerText = "No contacts available.";
        list_item_div.appendChild(col_1);
    }
    if (adminAccess) {
        //Render the 'Add Device' line
        const list_item_div = document.createElement("div");
        list_item_div.className = "list-item flex-row";
        list.appendChild(list_item_div);
        const addContact = document.createElement("p");
        addContact.innerText = "Add Contact";
        addContact.className = "list-col-1 link";
        addContact.addEventListener("click", add_org_contact);
        list_item_div.appendChild(addContact);
    }
};

//Quote / Order Details ======================================================================
const renderQuoteDetails = async () => {
    const res = await sendRequest("quote_details", `${id}`);

    data = res.data;

    const order_number = document.getElementById("order-number");
    order_number.innerText = `Order ${data.quote_number} Details`;

    renderDetailList("quote");
};

const renderOrderDetails = async () => {
    const res = await sendRequest("order_details", `${id}`);

    data = res.data;

    const order_number = document.getElementById("order-number");
    order_number.innerText = `Order ${data.order_number} Details`;

    renderDetailList("order");
};

const renderDetailList = (type) => {
    const list = document.getElementById("detail-list-items");
    list.innerHTML = "";
    if (!data.order_details) {
        data.order_details = [];
    }
    data.order_details.forEach((item, index) => {
        const list_item_div = document.createElement("div");
        list_item_div.className = "list-item flex-row";
        list.appendChild(list_item_div);

        const col_1 = document.createElement("div");
        col_1.className = "list-col-1 flex-column";
        list_item_div.appendChild(col_1);

        const metaData = document.createElement("p");
        metaData.className = "list-meta";
        metaData.innerText = `${item.created_by} | ${item.created_ts}`;
        col_1.appendChild(metaData);

        if (item.details) {
            const details = document.createElement("p");
            details.innerText = item.details;
            details.className = "list-details";
            col_1.appendChild(details);
        }

        if (item.attachment !== "?" && item.attachment) {
            const attachmentDiv = document.createElement("div");
            attachmentDiv.innerHTML = `<p class="docIcon"><i class="far fa-file-alt"></i></p><p class="filename">${item.attachment}</p><p class="remove-file link"><i class="fas fa-arrow-circle-down"></i></p>`;
            attachmentDiv.id = item.id;
            attachmentDiv.className = "flex-row attachment";
            attachmentDiv.addEventListener("click", () => {
                dwnldDetailAttachment(item.id, index);
            });
            col_1.appendChild(attachmentDiv);
        }

        if (type === "quote") {
            const col_2 = document.createElement("div");
            col_2.className = "list-col-2 inline-items";
            list_item_div.appendChild(col_2);

            const remove = document.createElement("p");
            remove.innerText = "Remove";
            remove.className = "link";
            remove.addEventListener("click", () => {
                removeDetail(item.id, index);
            });
            col_2.appendChild(remove);

            const edit = document.createElement("p");
            edit.innerText = "Edit";
            edit.className = "link";
            edit.addEventListener("click", () => {
                editDetail(item.id, index);
            });
            col_2.appendChild(edit);
        }
    });
};

//Manage Contacts ======================================================================
const renderOrgContacts = async () => {
    const res = await sendRequest("manage_contacts", "");

    data = res.data;

    image_option = userData.org_image_option;
    if (+image_option === 1) {
        const header = document.querySelector(".list-col-2");
        const imageHeader = document.createElement("h1");
        imageHeader.innerHTML = `${picsIcon} Access to Photos`;
        header.appendChild(imageHeader);
    } else {
        hideImageOpt.style.display = "none";
    }

    mdmBeta = userData.mdm_beta;

    sorted_array = data.contact_list ? data.contact_list.slice() : "";

    const customer = document.getElementById("customer-name");
    customer.innerText = data.customer_name ? data.customer_name : "Organization Unknown";

    sort();
};

//Messages ======================================================================
const renderMessage = async () => {
    const ts = getDate();
    const res = await sendRequest("get_messages", `${ts}`);

    json_data = res.data;

    const order = document.getElementById("messages-customer");
    const customer = document.createElement("h1");
    customer.innerText = json_data.customer;
    order.append(customer);

    renderOptions();

    renderMessages();

    refresh();
    timeout();
};

// Validation function, needs to be modified to fit our flow and variables
function fileValidation(filePath) {
    // Allowing file type by extension
    const allowedExtensions =
        /(\.doc|\.docx|\.odt|\.pdf|\.txt|\.csv|\.rtf|\.wps|\.wpd|\.xlsx|\.png|\.jpg|\.jpeg|\.zip)$/i;
    regex = /[\;\:\<\>\\/\%\$\*\,]/;
    const extcount = (filePath.match(/\./g) || []).length;
    const charcount = (filePath.match(regex) || []).length;
    // console.log(extcount);
    // console.log(charcount);

    if (!allowedExtensions.exec(filePath)) {
        return "File type not allowed.";
    }
    if (extcount > 1) {
        return "The filename you are uploading can not contain any periods, except for the file extension.";
    }
    if (charcount >= 1) {
        return "The filename you are uploading can not contain any special characters including ,;:<>/%$*";
    }
    return "File Valid";
}

//Device List ======================================================================
const renderDeviceList = async () => {
    const mdm_data = JSON.parse(myStorage.getItem("mdm_data"));

    const loaded = document.getElementById("loaded-list");
    loaded.style.display = "none";
    const lastUpdate = document.getElementById("lastUpdate");
    lastUpdate.innerText = mdm_data.last_update_ts;
    //If the current data is already stored in local storage, then just render them
    if (!mdm_data.update) {
        renderBlueprintList();
        renderDevices();
        return;
    }

    //Get Device and SKU data from FM
    data = {};
    cust_id = userData.id_customer;
    //Get MDM Device List from FM
    if (!data.device_list) {
        let assetList = [];
        let recordCount = 100;
        let count = 0;
        while (count < recordCount) {
            const offset = count + 1;
            const res = await getDevices(cust_id, offset, mdmType);
            count += res.dataInfo.returnedCount;
            recordCount = res.dataInfo.foundCount;
            const arrayOne = assetList.slice();
            const arrayTwo = res.data;
            assetList = arrayOne.concat(arrayTwo);
            // console.log("count: " + count + " records: " + recordCount)
            // loadingMessage.innerHTML = 'Loading devices...'
            // var percent = count / recordCount * 100;
            // var progress = document.querySelector('.meter span');
            // progress.style.width = Math.floor(percent) + '%';
        }
        data.device_list = assetList;
    }
    //Get SKU List from FM
    if (!data.sku_list) {
        let skuList = [];
        let recordCount = 100;
        let count = 0;
        while (count < recordCount) {
            const offset = count + 1;
            const res = await getSKU(cust_id, offset, mdmType);
            count += res.dataInfo.returnedCount;
            recordCount = res.dataInfo.foundCount;
            const arrayOne = skuList.slice();
            const arrayTwo = res.data;
            skuList = arrayOne.concat(arrayTwo);
            // console.log("count: " + count + " records: " + recordCount)
            // loadingMessage.innerHTML = 'Loading devices...'
            // var percent = count / recordCount * 100;
            // var progress = document.querySelector('.meter span');
            // progress.style.width = Math.floor(percent) + '%';
        }
        data.sku_list = skuList;
    }
    //Create and sort arrays
    device_array = data.device_list.slice();
    sku_array = data.sku_list.slice();
    //Loop through devices to count qty per sku
    for (const item of device_array) {
        const mdmID = item.fieldData.mdm_ID_MDM_DEVICE_SKU;
        const qty = sku_list[mdmID] ? sku_list[mdmID].quantity + 1 : 1;
        const bpID = item.fieldData.mdm_blueprint_id;
        const bpqty = sku_list[mdmID]?.bp_list[bpID] ? sku_list[mdmID].bp_list[bpID] + 1 : 1;
        const newSKU = {
            ...sku_list[mdmID],
            quantity: qty,
        };
        if (!newSKU.bp_list) {
            newSKU.bp_list = {};
        }
        newSKU.bp_list[bpID] = bpqty;
        sku_list[mdmID] = newSKU;
        bp_list[bpID] = item.fieldData.mdm_blueprint;
    }
    //Loop through the line items to set quantity for each
    sku_array.forEach((item, index, object) => {
        const id = item.fieldData.sku_ID;
        const quantity = sku_list[id] ? sku_list[id].quantity : 0;
        if (item.fieldData.sku_device_category === "Other" || quantity === 0) {
            item.fieldData.quantity = 0;
        } else {
            const addData = {
                ...item.fieldData,
                ...sku_list[id],
            };
            item.fieldData = addData;
        }
    });
    //Save to local storage
    // mdm_data.device_array = device_array;
    mdm_data.sku_array = sku_array;
    mdm_data.sku_list = sku_list;
    mdm_data.bp_list = bp_list;
    myStorage.setItem("mdm_data", JSON.stringify(mdm_data));
    // myStorage.setItem('device_array', JSON.stringify(device_array));
    // myStorage.setItem('sku_array', JSON.stringify(sku_array));
    // myStorage.setItem('sku_list', JSON.stringify(sku_list));
    // myStorage.setItem('bp_list', JSON.stringify(bp_list));
    if (mdm_data.mdm_types.length > 1) {
        renderMdmList();
    } else {
        const mdmFilter = document.getElementById("mdmFilter");
        mdmFilter.style.display = "none";
    }
    //Render Blueprint Filter List and Devices
    renderBlueprintList();
    renderDevices();
};
const renderMdmList = () => {
    const mdm_data = JSON.parse(myStorage.getItem("mdm_data"));
    const mdm_list = mdm_data.mdm_types;
    const mdmFilter = document.getElementById("mdmFilter");
    for (i = 0; i < mdm_list.length; i++) {
        const option = document.createElement("option");
        const val = mdm_list[i];
        const low = val.toLowerCase();
        option.setAttribute("id", low);
        option.setAttribute("value", low);
        option.innerText = val;
        if (mdmType === low) {
            option.setAttribute("selected", true);
        }
        mdmFilter.appendChild(option);
    }
    mdmFilter.addEventListener("change", (event) => {
        window.location.href = `./mdm_device_list.html?mdm_type=${event.target.value}`;
    });
};
const renderBlueprintList = () => {
    const mdm_data = JSON.parse(myStorage.getItem("mdm_data"));
    const bp_list = mdm_data.bp_list;
    const bpFilter = document.getElementById("bpFilter");
    for (item in bp_list) {
        const option = document.createElement("option");
        option.setAttribute("id", item);
        option.setAttribute("value", item);
        option.innerText = bp_list[item];
        option.setAttribute("selected", true);
        bpFilter.appendChild(option);
    }
    setupMultiSelect();
};
const sortDevices = () => {
    const sortColumn = document.getElementById("sortOption").value;
    const mdm_data = JSON.parse(myStorage.getItem("mdm_data"));
    const item_data = mdm_data.sku_array.slice();
    switch (sortColumn) {
        case "deviceFilter":
            item_data.sort(skuSort);
            break;
        case "ageFilter":
            item_data.sort(ageSort);
            break;
        case "valueFilter":
            item_data.sort(valueSort);
            break;
        default:
            break;
    }
    return item_data;
};
const renderDevices = () => {
    const mdm_data = JSON.parse(myStorage.getItem("mdm_data"));
    // var item_data = mdm_data.sku_array;
    const item_data = sortDevices();
    const loading = document.getElementById("loader");
    const loaded = document.getElementById("loaded-list");
    const resolved_list = document.querySelector("#resolved");
    resolved_list.innerHTML = "";
    const pending_list = document.querySelector("#pending");
    pending_list.innerHTML = "";

    const deviceSummary = {
        labels: ["Tablet", "Laptop", "Desktop", "Mobile", "Under Review"],
        datasets: [
            {
                label: "Device Total",
                data: [0, 0, 0, 0, 0],
                backgroundColor: [
                    "rgb(0, 121, 63)",
                    "rgb(159, 199, 47)",
                    "rgb(81, 96, 37)",
                    "rgb(148, 183, 166)",
                    "rgb(139, 139, 139)",
                ],
                hoverOffset: 4,
            },
        ],
    };
    const ageSummary = {
        labels: ["2 years or less", "3 years", "4 years", "5 years or older"],
        datasets: [
            {
                label: "Device Total",
                data: [0, 0, 0, 0],
                backgroundColor: [
                    "rgb(0, 121, 63)",
                    "rgb(220, 227, 36)",
                    "rgb(217, 117, 45)",
                    "rgb(181, 34, 35)",
                ],
                hoverOffset: 4,
            },
        ],
    };
    const valueSummary = {
        labels: ["2 years or less", "3 years", "4 years", "5 years or older"],
        datasets: [
            {
                label: "Potential Value",
                data: [0, 0, 0, 0],
                backgroundColor: [
                    "rgb(0, 121, 63)",
                    "rgb(220, 227, 36)",
                    "rgb(217, 117, 45)",
                    "rgb(181, 34, 35)",
                ],
                hoverOffset: 4,
            },
        ],
    };
    const osSummary = {
        labels: ["Current OS", "Legacy OS"],
        datasets: [
            {
                label: "Device Total",
                data: [0, 0],
                backgroundColor: ["rgb(0, 121, 63)", "rgb(181, 34, 35)"],
                hoverOffset: 4,
            },
        ],
    };
    const classSummary = {
        labels: ["Current", "Vintage", "Obsolete"],
        datasets: [
            {
                label: "Device Total",
                data: [0, 0, 0],
                backgroundColor: ["rgb(0, 121, 63)", "rgb(217, 117, 45)", "rgb(181, 34, 35)"],
                hoverOffset: 4,
            },
        ],
    };
    reqTotal = 0;
    let weightTotal = 0;
    let deviceTotal = 0;
    let ageTotal = 0;
    let ageCount = 0;
    let valueTotal = 0;
    let osTotal = 0;
    let colorTooltip = "";
    let colorIcon = "";
    let deviceIcon = "";
    const redIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2023 Fonticons, Inc.--><path fill="#B52223" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z"/></svg>';
    const greenIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2023 Fonticons, Inc.--><path fill="#00793F" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z"/></svg>';
    const orangeIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2023 Fonticons, Inc.--><path fill="#D9752C" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z"/></svg>';
    const yellowIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2023 Fonticons, Inc.--><path fill="#DCE324" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z"/></svg>';
    const greyIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2023 Fonticons, Inc.--><path fill="#8B8B8B" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z"/></svg>';
    const tabletIcon = '<img src="./img/tablet@2x.png" alt="Clarity">';
    const laptopIcon = '<img src="./img/laptop@2x.png" alt="Clarity">';
    const desktopIcon = '<img src="./img/desktop@2x.png" alt="Clarity">';
    const mobileIcon = '<img src="./img/mobile@2x.png" alt="Clarity">';
    const otherIcon = '<img src="./img/apple@2x.png" alt="Clarity">';

    for (const item_data_record of item_data) {
        const item = item_data_record.fieldData;
        const desktopFilter = document.getElementById("desktopFilter").selected;
        const laptopFilter = document.getElementById("laptopFilter").selected;
        const tabletFilter = document.getElementById("tabletFilter").selected;
        const mobileFilter = document.getElementById("mobileFilter").selected;
        const age2Filter = document.getElementById("age2Filter").selected;
        const age3Filter = document.getElementById("age3Filter").selected;
        const age4Filter = document.getElementById("age4Filter").selected;
        const age5Filter = document.getElementById("age5Filter").selected;

        let qtyTotal = item.quantity;
        if (item.bp_list) {
            for (bp in item.bp_list) {
                const isChecked = document.getElementById(bp).selected;
                if (!isChecked) {
                    qtyTotal -= item.bp_list[bp];
                }
            }
        }

        let filtered = true;
        //Device Type Filter
        if (item.sku_device_category === "Desktop" && !desktopFilter) {
            filtered = false;
        } else if (item.sku_device_category === "Laptop" && !laptopFilter) {
            filtered = false;
        } else if (item.sku_device_category === "Tablet" && !tabletFilter) {
            filtered = false;
        } else if (item.sku_device_category === "Mobile" && !mobileFilter) {
            filtered = false;
        }
        //Age Filter
        if (item.sku_age === "" && filtered) {
            filtered = true;
        } else if (item.sku_age >= 0 && item.sku_age < 3 && !age2Filter) {
            filtered = false;
        } else if (item.sku_age === 3 && !age3Filter) {
            filtered = false;
        } else if (item.sku_age === 4 && !age4Filter) {
            filtered = false;
        } else if (item.sku_age >= 5 && !age5Filter) {
            filtered = false;
        }

        if (qtyTotal && filtered) {
            //Update Chart Header Totals
            if (item.sku_weight_lbs) {
                weightTotal += item.sku_weight_lbs * qtyTotal;
            }
            if (item.sku_apple_support_classification === "Current") {
                classSummary.datasets[0].data[0] += qtyTotal;
            } else if (item.sku_apple_support_classification === "Vintage") {
                classSummary.datasets[0].data[1] += qtyTotal;
            } else if (item.sku_apple_support_classification === "Obsolete") {
                classSummary.datasets[0].data[2] += qtyTotal;
            }
            deviceTotal += qtyTotal;
            if (item.sku_age === "") {
                //do nothing
            } else {
                ageTotal += item.sku_age * qtyTotal;
                ageCount += qtyTotal;
            }
            if (item.sku_value) {
                valueTotal += item.sku_value * qtyTotal;
            }
            //Update Summary Datasets based on Age
            if (item.sku_age === "") {
                //do nothing
            } else if (item.sku_age <= 2) {
                valueSummary.datasets[0].data[0] += item.sku_value * qtyTotal;
                ageSummary.datasets[0].data[0] += qtyTotal;
            } else if (item.sku_age === 3) {
                valueSummary.datasets[0].data[1] += item.sku_value * qtyTotal;
                ageSummary.datasets[0].data[1] += qtyTotal;
            } else if (item.sku_age === 4) {
                valueSummary.datasets[0].data[2] += item.sku_value * qtyTotal;
                ageSummary.datasets[0].data[2] += qtyTotal;
            } else if (item.sku_age >= 5) {
                valueSummary.datasets[0].data[3] += item.sku_value * qtyTotal;
                ageSummary.datasets[0].data[3] += qtyTotal;
            }
            //Update Summary Datasets based on Supported OS
            if (item.sku_supported_os_type === "Current OS") {
                osSummary.datasets[0].data[0] += qtyTotal;
            } else if (item.sku_supported_os_type === "Legacy OS") {
                osSummary.datasets[0].data[1] += qtyTotal;
            }
            //Set Icon based on Health Rating
            if (item.sku_health_rating === "Green") {
                colorIcon = greenIcon;
                colorTooltip = "2 years old or less AND/OR Current";
            } else if (item.sku_health_rating === "Yellow") {
                colorIcon = yellowIcon;
                colorTooltip = "3 years old AND/OR Current";
            } else if (item.sku_health_rating === "Orange") {
                colorIcon = orangeIcon;
                colorTooltip = "4 years old AND/OR Vintage";
            } else if (item.sku_health_rating === "Red") {
                colorIcon = redIcon;
                colorTooltip = "5 years or older AND/OR Obsolete / Legacy";
            } else {
                colorIcon = greyIcon;
                colorTooltip = "Under Review";
            }
            //Set Icon based on Device Type, and update Summary dataset
            if (item.missing_device) {
                deviceSummary.datasets[0].data[4] += qtyTotal;
            } else if (item.sku_device_category === "Tablet") {
                deviceSummary.datasets[0].data[0] += qtyTotal;
            } else if (item.sku_device_category === "Laptop") {
                deviceSummary.datasets[0].data[1] += qtyTotal;
            } else if (item.sku_device_category === "Desktop") {
                deviceSummary.datasets[0].data[2] += qtyTotal;
            } else if (item.sku_device_category === "Mobile") {
                deviceSummary.datasets[0].data[3] += qtyTotal;
            }
            if (item.sku_device_category === "Tablet") {
                deviceIcon = tabletIcon;
            } else if (item.sku_device_category === "Laptop") {
                deviceIcon = laptopIcon;
            } else if (item.sku_device_category === "Desktop") {
                deviceIcon = desktopIcon;
            } else if (item.sku_device_category === "Mobile") {
                deviceIcon = mobileIcon;
            } else {
                deviceIcon = otherIcon;
            }
            //Render SKU List Item
            const id = item.sku_ID;
            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row";
            list_item_div.id = `a${id}`;
            if (item.missing_device) {
                pending_list.appendChild(list_item_div);
                reviewDevices = true;
            } else {
                resolved_list.appendChild(list_item_div);
            }
            const col_1 = document.createElement("div");
            col_1.innerHTML = `<a href="#">${colorIcon}<div>${deviceIcon}</div></a><div class="ratingHover">${colorTooltip}</div>`;
            col_1.className = "list-col-1";
            list_item_div.appendChild(col_1);
            // var col_2 = document.createElement("div");
            // col_2.innerHTML = deviceIcon;
            // col_2.className = 'list-col-2';
            // list_item_div.appendChild(col_2);
            const col_3 = document.createElement("div");
            col_3.className = "list-col-3";
            list_item_div.appendChild(col_3);
            const deviceName = document.createElement("p");
            deviceName.innerText = item.sku_model;
            deviceName.className = item.missing_device ? "device-Name pending" : "device-Name";
            col_3.appendChild(deviceName);
            if (item.sku_specs) {
                const deviceSpecs = document.createElement("p");
                deviceSpecs.innerText = item.sku_specs;
                deviceSpecs.className = "device-specs";
                col_3.appendChild(deviceSpecs);
            }

            if (item.missing_device) {
                const col_4 = document.createElement("p");
                col_4.innerText = qtyTotal;
                col_4.className = "list-col-4";
                list_item_div.appendChild(col_4);
            } else {
                const col_4 = document.createElement("p");
                col_4.innerText = item.sku_age;
                col_4.className = "list-col-4";
                list_item_div.appendChild(col_4);

                const col_5 = document.createElement("div");
                col_5.className = "list-col-5";
                list_item_div.appendChild(col_5);
                const qtyInput = document.createElement("input");
                const qtyInputValue = item.req_quantity ? item.req_quantity : qtyTotal;
                qtyInput.setAttribute("value", qtyInputValue);
                qtyInput.setAttribute("type", "text");
                qtyInput.setAttribute("id", item.sku_ID);
                qtyInput.className = "qteReqOn";
                qtyInput.addEventListener("change", (event) => {
                    updateReqQty(item.sku_ID, event.target.value);
                });
                col_5.appendChild(qtyInput);
                const qtyDivider = document.createElement("p");
                qtyDivider.innerHTML = "/&nbsp";
                qtyDivider.className = "qteReqOn";
                col_5.appendChild(qtyDivider);
                const qtyText = document.createElement("p");
                qtyText.innerText = qtyTotal;
                col_5.appendChild(qtyText);

                const col_6 = document.createElement("p");
                col_6.innerHTML = item.sku_req_price_check
                    ? "Request a Quote"
                    : currency.format(item.sku_value);
                col_6.className = "list-col-6";
                list_item_div.appendChild(col_6);

                const col_7 = document.createElement("p");
                col_7.className = "list-col-7";
                const reqValue = qtyInputValue * item.sku_value;
                const index = mdm_data.sku_array.findIndex(
                    (elem) => elem.fieldData.sku_ID === item.sku_ID,
                );
                mdm_data.sku_array[index].fieldData.req_quantity = qtyInputValue;
                mdm_data.sku_array[index].fieldData.req_value = reqValue;
                if (mdm_data.qte_req === true && !item.sku_req_price_check) {
                    col_7.innerText = currency.format(reqValue);
                    reqTotal += reqValue;
                } else if (!item.sku_req_price_check) {
                    col_7.innerText = currency.format(qtyTotal * item.sku_value);
                } else {
                    col_7.innerText = "Request a Quote";
                }
                list_item_div.appendChild(col_7);
            }
        }
    }
    if (mdm_data.qte_req === true) {
        const hideItems = document.querySelectorAll(".qteReqOff");
        const showItems = document.querySelectorAll(".qteReqOn");
        for (const item of hideItems) {
            item.style.display = "none";
        }
        for (const item of showItems) {
            item.style.display = "flex";
        }
    } else {
        const showItems = document.querySelectorAll(".qteReqOff");
        const hideItems = document.querySelectorAll(".qteReqOn");
        for (const item of hideItems) {
            item.style.display = "none";
        }
        for (const item of showItems) {
            item.style.display = "";
        }
    }
    if (!reviewDevices) {
        const reviewHeader = document.getElementById("pending-header");
        reviewHeader.style.display = "none";
    }
    reqTotalDisplay.innerText = currency.format(reqTotal);
    mdm_data.req_total = reqTotal;
    //Render Charts
    const deviceChartHeader = document.querySelector("#device-summary h1");
    const ageChartHeader = document.querySelector("#age-summary h1");
    const valueChartHeader = document.querySelector("#value-summary h1");
    const osChartHeader = document.querySelector("#os-summary h1");
    const weightHeader = document.querySelector("#weight-summary h1");
    aveAvg = ageTotal / ageCount;
    let valueTotalShort = 0;
    if (valueTotal < 1000) {
        valueTotalShort = valueTotal;
    } else if (valueTotal < 1000000) {
        const val = valueTotal / 1000;
        valueTotalShort = `${val.toFixed(1)}k`;
    } else if (valueTotal < 1000000000) {
        const val = valueTotal / 1000000;
        valueTotalShort = `${val.toFixed(1)}m`;
    } else if (valueTotal < 1000000000000) {
        const val = valueTotal / 1000000000;
        valueTotalShort = `${val.toFixed(1)}b`;
    } else {
        valueTotalShort = "1t+";
    }
    osCount = osSummary.datasets[0].data[0] + osSummary.datasets[0].data[1];
    osTotal = osSummary.datasets[0].data[0] / osCount;
    deviceChartHeader.innerText = deviceTotal ? deviceTotal : 0;
    ageChartHeader.innerText = aveAvg ? aveAvg.toFixed(1) : 0;
    valueChartHeader.innerText = valueTotal ? `$${valueTotalShort}` : "$0";
    osChartHeader.innerText = osTotal ? `${osTotal.toFixed(2) * 100}%` : 0;
    weightHeader.innerText = weightTotal ? `${weightTotal.toFixed(0)} lbs` : 0;

    document.getElementById("deviceChart").remove();
    const canvas1 = document.createElement("canvas");
    canvas1.setAttribute("id", "deviceChart");
    document.querySelector("#device-summary").appendChild(canvas1);

    document.getElementById("ageChart").remove();
    const canvas2 = document.createElement("canvas");
    canvas2.setAttribute("id", "ageChart");
    document.querySelector("#age-summary").appendChild(canvas2);

    document.getElementById("valueChart").remove();
    const canvas3 = document.createElement("canvas");
    canvas3.setAttribute("id", "valueChart");
    document.querySelector("#value-summary").appendChild(canvas3);

    document.getElementById("osChart").remove();
    const canvas4 = document.createElement("canvas");
    canvas4.setAttribute("id", "osChart");
    document.querySelector("#os-summary").appendChild(canvas4);

    document.getElementById("classificationChart").remove();
    const canvas5 = document.createElement("canvas");
    canvas5.setAttribute("id", "classificationChart");
    document.querySelector(".chartWrapper").appendChild(canvas5);

    const ctxDevice = document.getElementById("deviceChart");
    deviceChart = new Chart(ctxDevice, {
        type: "pie",
        data: deviceSummary,
        options: {
            cutout: "80%",
            plugins: {
                legend: {
                    display: false,
                },
            },
        },
    });
    const ctxAge = document.getElementById("ageChart");
    ageChart = new Chart(ctxAge, {
        type: "pie",
        data: ageSummary,
        options: {
            cutout: "80%",
            plugins: {
                legend: {
                    display: false,
                },
            },
        },
    });
    const ctxValue = document.getElementById("valueChart");
    valueChart = new Chart(ctxValue, {
        type: "pie",
        data: valueSummary,
        options: {
            cutout: "80%",
            plugins: {
                legend: {
                    display: false,
                },
            },
        },
    });
    const ctxOS = document.getElementById("osChart");
    osChart = new Chart(ctxOS, {
        type: "pie",
        data: osSummary,
        options: {
            cutout: "80%",
            plugins: {
                legend: {
                    display: false,
                },
            },
        },
    });
    const ctxClass = document.getElementById("classificationChart");
    classChart = new Chart(ctxClass, {
        type: "bar",
        data: classSummary,
        options: {
            indexAxis: "y",
            barThickness: 22,
            elements: {
                bar: {
                    borderRadius: 3,
                },
            },
            maintainAspectRatio: false,
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        display: false,
                    },
                    grid: {
                        display: false,
                    },
                },
                y: {
                    grid: {
                        display: false,
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
            },
        },
    });

    loading.style.display = "none";
    loaded.style.display = "";
    myStorage.setItem("mdm_data", JSON.stringify(mdm_data));
};

//Deployment Services Asset List ======================================================================
const renderDSAssetList = async () => {
    const loaded = document.getElementById("loaded-list");
    const loading = document.getElementById("loader");
    const loadingMessage = document.getElementById("loadingMessage");
    loaded.style.display = "none";

    data = {};
    cust_id = userData.id_customer;

    if (!data.asset_list) {
        let assetList = [];
        let recordCount = 100;
        let count = 0;
        while (count < recordCount) {
            const offset = count + 1;
            const res = await getDsAssets(cust_id, offset);
            count += res.dataInfo.returnedCount;
            recordCount = res.dataInfo.foundCount;
            const arrayOne = assetList.slice();
            const arrayTwo = res.data;
            assetList = arrayOne.concat(arrayTwo);
            loadingMessage.innerHTML = `Loading ${count} of ${recordCount} devices...`;
            const percent = (count / recordCount) * 100;
            const progress = document.querySelector(".meter span");
            progress.style.width = `${Math.floor(percent)}%`;
        }
        item_array = assetList;
    }

    myStorage.setItem("ds_data", JSON.stringify(item_array));
    loading.style.display = "none";
    loaded.style.display = "";
    // viewChange('select')
    renderDSAsset();
};

const renderDSAsset = (array) => {
    const device_list = document.querySelector("#list-content");
    // var print_list = document.querySelector('#print-content') ;
    device_list.innerText = "";
    // print_list.innerText = '';
    // console.log(selectedArray)

    if (array) {
        array.forEach((item_data, index) => {
            const item = item_data.fieldData;

            //Render Asset
            const list_item_div = document.createElement("div");
            list_item_div.className = "list-item flex-row";
            list_item_div.id = `id-${item.prov_records}`;
            device_list.appendChild(list_item_div);

            // var col_1 = document.createElement("p");
            // col_1.innerHTML = selectedArray.includes('id-' + item.prov_ID) ? checkIcon : squareIcon;
            // col_1.className = 'list-col-1 icon';
            // list_item_div.appendChild(col_1);
            // col_1.addEventListener("click", selectToggle('id-' + item.prov_ID));

            const col_1 = document.createElement("p");
            col_1.innerText = item.prov_serial_number;
            col_1.className = item.prov_decomission ? "list-col-1 inactive" : "list-col-1";
            list_item_div.appendChild(col_1);

            const col_2 = document.createElement("p");
            col_2.innerText = item.prov_customer_tag;
            col_2.className = "list-col-2";
            list_item_div.appendChild(col_2);

            const col_3 = document.createElement("p");
            col_3.innerText = `${item.prov_student_last}, ${item.prov_student_first}`;
            col_3.className = "list-col-3";
            list_item_div.appendChild(col_3);

            const col_4 = document.createElement("p");
            col_4.innerHTML = item.prov_student_grad_year;
            col_4.className = "list-col-4";
            list_item_div.appendChild(col_4);

            const col_5 = document.createElement("p");
            col_5.innerHTML = "Edit";
            col_5.className = "list-col-5 link";
            list_item_div.appendChild(col_5);
            col_5.addEventListener("click", (event) => editAsset(item.prov_record, index));

            const col_6 = document.createElement("p");
            col_6.innerHTML = "Download PDF Tag";
            col_6.className = "list-col-6 link";
            list_item_div.appendChild(col_6);
            col_6.addEventListener("click", (event) =>
                downloadTag(item.prov_record, item.prov_serial_number),
            );
        });
    }
};
